import { ThreadListPrimitive, useAuiState } from '@assistant-ui/react';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ButtonBase from '@mui/material/ButtonBase';

import { AgentWorkbenchThreadListItem } from './agent-workbench-thread-list-item.js';

interface AgentWorkbenchThreadListProps {
	/**
	 * Invoked when the user resumes a conversation, so the caller can dismiss the
	 * drawer. The conversation id is undefined while the thread's server record
	 * is still being created.
	 */
	onSelectThread?: (conversationId: string | undefined) => void;
	/** Invoked when the user starts a new conversation, so the caller can dismiss the drawer. */
	onNewThread?: () => void;
	allowThreadCreation?: boolean;
	allowThreadDeletion?: boolean;
}

/**
 * Scrollable list of every conversation the user owns, newest first. Resuming a
 * row switches the active conversation; the header control starts a fresh one.
 * Reads thread data from the surrounding assistant runtime, so it must render
 * inside an `AssistantRuntimeProvider`.
 *
 * @param props - List props.
 * @param props.onSelectThread - Forwarded to each row to dismiss the drawer.
 * @param props.onNewThread - Wired to the new-conversation control to dismiss the drawer.
 */
export function AgentWorkbenchThreadList({
	onSelectThread,
	onNewThread,
	allowThreadCreation = true,
	allowThreadDeletion = true,
}: AgentWorkbenchThreadListProps) {
	const isEmpty = useAuiState((s) => s.threads.threadIds.length === 0);

	return (
		<ThreadListPrimitive.Root className='flex min-h-0 grow flex-col bg-gray-2'>
			<div className='flex shrink-0 items-center justify-between gap-2 border-b border-gray-a6 px-3 py-2'>
				<h2 className='truncate font-medium text-gray-12'>Conversations</h2>
				{allowThreadCreation && (
					<ThreadListPrimitive.New asChild onClick={onNewThread}>
						<ButtonBase
							className='flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-blue-11 transition-colors duration-150 hover:bg-blue-a3 motion-reduce:transition-none'
							aria-label='New conversation'
						>
							<AddIcon style={{ fontSize: 16 }} />
							New
						</ButtonBase>
					</ThreadListPrimitive.New>
				)}
			</div>

			{isEmpty ?
				<div className='flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center'>
					<ChatBubbleOutlineIcon className='text-gray-8' style={{ fontSize: 24 }} />
					<p className='text-gray-12'>No conversations yet</p>
					<p className='text-gray-11'>Ask a question and it’s saved here — resume it from anywhere in the app.</p>
				</div>
			:	<div className='flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-1.5'>
					<ThreadListPrimitive.Items>
						{() => <AgentWorkbenchThreadListItem onSelectThread={onSelectThread} allowDelete={allowThreadDeletion} />}
					</ThreadListPrimitive.Items>
				</div>
			}
		</ThreadListPrimitive.Root>
	);
}
